import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, getErrorMessage } from '../lib/api';
import { toast } from '../utils/toast';
import { Alert } from './ui/Alert';
import { CheckCircle2, CreditCard, Smartphone, Hash, UploadCloud } from 'lucide-react';
import type { Product, Application, PaymentMethod } from '../types';

interface SubscriptionCheckoutProps {
  product: Product;
  planId: string;
  applications: Application[];
  environment: 'sandbox' | 'production';
  onSuccess: () => void;
}

export const SubscriptionCheckout: React.FC<SubscriptionCheckoutProps> = ({
  product,
  planId,
  applications,
  environment,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [paymentPhoneNumber, setPaymentPhoneNumber] = useState('');
  const [paymentProofUploaded, setPaymentProofUploaded] = useState(false);
  const [emisReference, setEmisReference] = useState<{
    entity: string;
    reference: string;
    amount: string;
  } | null>(null);

  const { data: paymentMethodsData } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: async () => {
      try {
        const response = await api.get<{ data: PaymentMethod[] }>('/payment-methods');
        return response.data;
      } catch (e) {
        // Fallback mock
        return {
          data: [
            {
              id: '1',
              name: 'Transferência Bancária',
              type: 'bank_transfer',
              status: 'active',
              config: {
                iban: 'AO06 0000 0000 0000 0000 0000 0',
                bank_name: 'Banco Teste',
                account_name: 'API Marketplace LDA',
                instructions: 'Faça a transferência e anexe o comprovativo.',
              },
            },
            {
              id: '2',
              name: 'Referência EMIS',
              type: 'emis_reference',
              status: 'active',
              config: {
                instructions: 'Use a referência gerada no seu Multicaixa ou App Bancária.',
              },
            },
            {
              id: '3',
              name: 'Multicaixa Express',
              type: 'real_time',
              status: 'active',
              config: {
                instructions: 'Insira o seu número de telemóvel associado ao Multicaixa Express.',
              },
            },
          ] as PaymentMethod[],
        };
      }
    },
    enabled: environment === 'production' && step === 2,
  });

  const paymentMethods = paymentMethodsData?.data?.filter((m) => m.status === 'active') || [];
  const selectedPaymentMethod = paymentMethods.find((m) => m.id === selectedPaymentMethodId);

  const subscribeMutation = useMutation({
    mutationFn: async (payload: any) => {
      const response = await api.post('/subscriptions', payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Subscrição criada com sucesso!');
      onSuccess();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleNextStep = () => {
    if (step === 1) {
      if (!selectedApplicationId) {
        toast.warning('Selecione uma aplicação');
        return;
      }
      if (environment === 'sandbox') {
        // Bypass payment in sandbox
        handleSubscribe();
      } else {
        setStep(2);
      }
    } else if (step === 2) {
      if (!selectedPaymentMethodId) {
        toast.warning('Selecione um método de pagamento');
        return;
      }
      if (selectedPaymentMethod?.type === 'emis_reference') {
        // Simulate generating EMIS reference
        setEmisReference({
          entity: '00000',
          reference: Math.floor(100000000 + Math.random() * 900000000)
            .toString()
            .replace(/(\d{3})(?=\d)/g, '$1 '),
          amount: '10.000,00 Kz', // Mock amount
        });
      }
      setStep(3);
    }
  };

  const handleSubscribe = () => {
    const payload: any = {
      product_id: product.id,
      plan_id: planId,
      application_id: selectedApplicationId,
    };

    if (environment === 'production' && selectedPaymentMethod) {
      payload.payment_method_id = selectedPaymentMethod.id;

      if (selectedPaymentMethod.type === 'bank_transfer') {
        payload.payment_status = 'pending_approval';
        payload.payment_details = { proof_uploaded: paymentProofUploaded };
      } else if (selectedPaymentMethod.type === 'real_time') {
        payload.payment_status = 'paid'; // Simulated success
        payload.payment_details = { phone_number: paymentPhoneNumber };
      } else if (selectedPaymentMethod.type === 'emis_reference') {
        payload.payment_status = 'pending';
        payload.payment_details = emisReference;
      }
    }

    subscribeMutation.mutate(payload);
  };

  const renderPaymentIcon = (type: string) => {
    switch (type) {
      case 'bank_transfer':
        return <CreditCard className="w-6 h-6 text-blue-600" />;
      case 'real_time':
        return <Smartphone className="w-6 h-6 text-green-600" />;
      case 'emis_reference':
        return <Hash className="w-6 h-6 text-purple-600" />;
      default:
        return <CreditCard className="w-6 h-6" />;
    }
  };

  return (
    <div className="card-modern p-8 max-w-2xl mx-auto">
      {subscribeMutation.isError && (
        <Alert variant="error" className="mb-6">
          {getErrorMessage(subscribeMutation.error)}
        </Alert>
      )}

      {/* Stepper */}
      {environment === 'production' && (
        <div className="flex items-center justify-between mb-8 relative">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 dark:bg-slate-800 -z-10"></div>
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-primary-600 -z-10 transition-all duration-300"
            style={{ width: `${((step - 1) / 2) * 100}%` }}
          ></div>

          {[
            { num: 1, label: 'Aplicação' },
            { num: 2, label: 'Pagamento' },
            { num: 3, label: 'Confirmação' },
          ].map((s) => (
            <div key={s.num} className="flex flex-col items-center bg-white dark:bg-slate-900 px-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step >= s.num ? 'bg-primary-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}
              >
                {step > s.num ? <CheckCircle2 className="w-4 h-4" /> : s.num}
              </div>
              <span
                className={`text-xs mt-1 ${step >= s.num ? 'font-semibold text-primary-900 dark:text-primary-300' : 'text-slate-500 dark:text-slate-400'}`}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-6">
        {/* Step 1: Application Selection */}
        {step === 1 && (
          <div className="animate-fade-in">
            <div className="p-4 rounded-xl mb-6 bg-primary-50/70 dark:bg-primary-950/30 border border-primary-200 dark:border-primary-800/60">
              <p className="text-slate-700 dark:text-slate-300 m-0 text-sm">
                Está prestes a subscrever <strong>{product.name}</strong> no ambiente{' '}
                <strong>{environment === 'sandbox' ? 'Sandbox' : 'Produção'}</strong>.
              </p>
            </div>

            <div>
              <label htmlFor="application" className="label-modern">
                Selecionar Aplicação ({environment === 'sandbox' ? 'Sandbox' : 'Produção'}) *
              </label>
              <select
                id="application"
                value={selectedApplicationId}
                onChange={(e) => setSelectedApplicationId(e.target.value)}
                className="input-modern"
                required
              >
                <option value="">-- Selecione uma aplicação --</option>
                {applications?.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.name} ({app.sandbox_mode ? 'Sandbox' : 'Produção'})
                  </option>
                ))}
              </select>
              {(!applications || applications.length === 0) && (
                <Alert variant="warning" className="mt-3">
                  Não tem aplicações no ambiente{' '}
                  <strong>{environment === 'sandbox' ? 'Sandbox' : 'Produção'}</strong>.{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/applications')}
                    className="underline font-medium"
                  >
                    Crie uma aplicação
                  </button>
                  .
                </Alert>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Payment Method Selection */}
        {step === 2 && environment === 'production' && (
          <div className="animate-fade-in">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
              Selecione o Método de Pagamento
            </h3>
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <label
                  key={method.id}
                  className={`flex items-start gap-4 p-4 border rounded-xl cursor-pointer transition-all ${
                    selectedPaymentMethodId === method.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/40 ring-1 ring-primary-500'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/60'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment_method"
                    value={method.id}
                    checked={selectedPaymentMethodId === method.id}
                    onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {renderPaymentIcon(method.type)}
                      <span className="font-semibold text-slate-900 dark:text-white">
                        {method.name}
                      </span>
                    </div>
                    {selectedPaymentMethodId === method.id && method.config?.instructions && (
                      <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 p-2 bg-white dark:bg-slate-800 rounded border border-slate-100 dark:border-slate-700">
                        {method.config.instructions}
                      </p>
                    )}
                  </div>
                </label>
              ))}
              {paymentMethods.length === 0 && (
                <Alert variant="warning">Nenhum método de pagamento disponível no momento.</Alert>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Payment Details / Confirmation */}
        {step === 3 && environment === 'production' && selectedPaymentMethod && (
          <div className="animate-fade-in space-y-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              Concluir Pagamento
            </h3>

            {/* Bank Transfer Flow */}
            {selectedPaymentMethod.type === 'bank_transfer' && (
              <div className="space-y-4">
                <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                  <h4 className="font-semibold text-sm text-slate-700 dark:text-slate-300 mb-2">
                    Dados Bancários
                  </h4>
                  <p className="text-sm text-slate-800 dark:text-slate-200">
                    <strong>Banco:</strong> {selectedPaymentMethod.config?.bank_name}
                  </p>
                  <p className="text-sm text-slate-800 dark:text-slate-200">
                    <strong>Titular:</strong> {selectedPaymentMethod.config?.account_name}
                  </p>
                  <p className="text-sm text-slate-800 dark:text-slate-200">
                    <strong>IBAN:</strong> {selectedPaymentMethod.config?.iban}
                  </p>
                </div>

                <div>
                  <label className="label-modern">Anexar Comprovativo *</label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-slate-300 dark:border-slate-700 border-dashed rounded-xl hover:border-primary-500 hover:bg-primary-50/40 dark:hover:bg-primary-950/20 transition-colors">
                    <div className="space-y-1 text-center">
                      <UploadCloud className="mx-auto h-12 w-12 text-slate-400" />
                      <div className="flex text-sm text-slate-600 dark:text-slate-400">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white dark:bg-slate-800 rounded-md font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 p-1"
                        >
                          <span>Fazer upload do ficheiro</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            onChange={(e) => setPaymentProofUploaded(!!e.target.files?.length)}
                          />
                        </label>
                        <p className="pl-1 pt-1">ou arraste e solte</p>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        PDF, PNG, JPG até 5MB
                      </p>
                    </div>
                  </div>
                  {paymentProofUploaded && (
                    <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2 flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> Comprovativo anexado com sucesso.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Real-time Flow */}
            {selectedPaymentMethod.type === 'real_time' && (
              <div>
                <label className="label-modern">Número de Telemóvel *</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                    +244
                  </span>
                  <input
                    type="tel"
                    className="input-modern rounded-l-none"
                    placeholder="9XX XXX XXX"
                    value={paymentPhoneNumber}
                    onChange={(e) => setPaymentPhoneNumber(e.target.value)}
                    required
                  />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Receberá uma notificação no seu telemóvel para confirmar o pagamento.
                </p>
              </div>
            )}

            {/* EMIS Flow */}
            {selectedPaymentMethod.type === 'emis_reference' && emisReference && (
              <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm text-center">
                <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-6">
                  Referência de Pagamento
                </h4>
                <div className="space-y-4 max-w-xs mx-auto">
                  <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
                    <span className="text-slate-500 dark:text-slate-400">Entidade:</span>
                    <span className="font-mono font-bold text-slate-900 dark:text-white">
                      {emisReference.entity}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
                    <span className="text-slate-500 dark:text-slate-400">Referência:</span>
                    <span className="font-mono font-bold text-slate-900 dark:text-white">
                      {emisReference.reference}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
                    <span className="text-slate-500 dark:text-slate-400">Montante:</span>
                    <span className="font-bold text-primary-600 dark:text-primary-400">
                      {emisReference.amount}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-6">
                  A sua subscrição ficará pendente até confirmação do pagamento.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-800 mt-8">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep((step - 1) as 1 | 2)}
              className="btn-base btn-md btn-secondary flex-1"
            >
              Voltar
            </button>
          )}
          {step === 1 && (
            <button
              type="button"
              onClick={() => {
                const targetAppId = selectedApplicationId || applications?.[0]?.id;
                if (targetAppId) {
                  navigate(`/applications/${targetAppId}/products/${product.id}`);
                } else {
                  navigate(-1);
                }
              }}
              className="btn-base btn-md btn-secondary flex-1"
            >
              Cancelar
            </button>
          )}

          <button
            type="button"
            onClick={
              step === 3 || (step === 1 && environment === 'sandbox')
                ? handleSubscribe
                : handleNextStep
            }
            disabled={
              subscribeMutation.isPending ||
              (step === 1 && !selectedApplicationId) ||
              (step === 2 && !selectedPaymentMethodId) ||
              (step === 3 &&
                selectedPaymentMethod?.type === 'bank_transfer' &&
                !paymentProofUploaded) ||
              (step === 3 && selectedPaymentMethod?.type === 'real_time' && !paymentPhoneNumber)
            }
            className="btn-base btn-md btn-primary flex-1"
          >
            {subscribeMutation.isPending ? (
              <span className="flex items-center gap-2 justify-center">
                <span className="spinner"></span>Processando...
              </span>
            ) : step === 3 || (step === 1 && environment === 'sandbox') ? (
              `Confirmar Subscrição — ${environment === 'sandbox' ? 'Sandbox' : 'Produção'}`
            ) : (
              'Continuar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
